import {
  Monitor,
  Network,
  Wrench,
  ShieldCheck,
  Phone,
  Mail,
  MapPin,
  Clock,
  ArrowRight,
  CheckCircle2,
  Cpu,
  HardDrive,
  Wifi,
} from "lucide-react";
import { Link } from "react-router-dom";

const services = [
  {
    icon: Wrench,
    title: "PC- & Notebook-Reparatur",
    desc: "Fehlerdiagnose, Hardware-Tausch, SSD-Upgrades und Datenrettung – schnell und transparent.",
  },
  {
    icon: Network,
    title: "Netzwerke & WLAN",
    desc: "Planung, Aufbau und Optimierung kabelgebundener Netzwerke und WLAN für Büro und Zuhause.",
  },
  {
    icon: Cpu,
    title: "Soft- & Hardware-Beratung",
    desc: "Passgenaue Empfehlungen statt Schubladenlösungen – auf Ihren Arbeitsalltag zugeschnitten.",
  },
  {
    icon: Wifi,
    title: "Telekommunikation & VoIP",
    desc: "Telefonanlagen, ISDN-Migration und VoIP-Einrichtung – Sie sind immer erreichbar.",
  },
  {
    icon: HardDrive,
    title: "Server & Administration",
    desc: "Einrichtung, Wartung und Betreuung kleiner Server-Umgebungen – inklusive Backup-Konzept.",
  },
  {
    icon: ShieldCheck,
    title: "IT-Sicherheit",
    desc: "Schutz vor Datenverlust, Viren und Phishing – mit Sicherheitskonzept und regelmäßigen Checks.",
  },
];

const advantages = [
  "Über 20 Jahre Erfahrung als Diplom-Ingenieur",
  "Persönliche Betreuung – kein Callcenter",
  "Transparente Festpreise & faire Beratung",
  "Vor-Ort-Service im Raum Niederrhein",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none" />
        <div className="absolute inset-0 bg-radial-fade pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-5 pt-16 pb-20 lg:px-8 lg:pt-24 lg:pb-28">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            <div>
              <span
                className="fade-up inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary"
                data-testid="hero-eyebrow"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                IT-Dienstleistungen seit 2004
              </span>
              <h1
                className="fade-up delay-1 mt-6 font-display text-4xl font-extrabold leading-[1.05] text-foreground sm:text-5xl lg:text-6xl"
                data-testid="hero-title"
              >
                Wenn Ihre IT streikt –{" "}
                <span className="text-primary">bin ich da.</span>
              </h1>
              <p
                className="fade-up delay-2 mt-6 max-w-xl text-base text-muted-foreground sm:text-lg"
                data-testid="hero-subtitle"
              >
                Ihr persönlicher IT-Experte am Niederrhein. Computer, Netzwerke und
                Telekommunikation – kompetent, ehrlich und ohne Fachchinesisch.
                Diplom-Ingenieur mit über zwei Jahrzehnten Erfahrung.
              </p>

              <div className="fade-up delay-3 mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="tel:028349448700"
                  data-testid="hero-call-button"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary/90 hover:scale-[1.02] transition"
                >
                  <Phone className="h-4 w-4" /> 02834 9448700
                </a>
                <a
                  href="#leistungen"
                  data-testid="hero-services-button"
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-7 py-3.5 text-sm font-semibold text-foreground hover:border-primary hover:text-primary transition"
                >
                  Leistungen ansehen <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <div className="fade-up delay-4 mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> Diplom-Ingenieur
                </span>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> Persönlicher Ansprechpartner
                </span>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> Vor-Ort-Service
                </span>
              </div>
            </div>

            <div className="fade-up delay-2 relative">
              <div className="relative rounded-3xl border border-border bg-white p-6 shadow-2xl shadow-primary/5 sm:p-8">
                <div className="flex items-center gap-1">
                  <div className="leading-tight">
                    <div className="font-display text-xl font-bold">Computer Hellen</div>
                    <div className="text-sm text-muted-foreground">Dipl.-Ing. Alexander Hellen</div>
                  </div>
                  <img src="/assets/logo-mouse.png" alt="Computer Hellen" className="h-16 w-auto -ml-5 -mt-4" />
                </div>
                <div className="mt-6 grid gap-4">
                  <div className="flex items-start gap-3 rounded-xl bg-secondary/60 p-4">
                    <Clock className="mt-0.5 h-5 w-5 text-primary" />
                    <div>
                      <div className="text-sm font-semibold">Erreichbarkeit</div>
                      <div className="text-sm text-muted-foreground">
                        Mo – Fr · 10:00–12:00 & 15:00–17:00 Uhr
                      </div>
                    </div>
                  </div>
                  <a
                    href="tel:028349448700"
                    className="flex items-start gap-3 rounded-xl bg-secondary/60 p-4 hover:bg-secondary transition"
                    data-testid="hero-card-phone"
                  >
                    <Phone className="mt-0.5 h-5 w-5 text-primary" />
                    <div>
                      <div className="text-sm font-semibold">Telefon</div>
                      <div className="text-sm text-muted-foreground">02834 9448700</div>
                    </div>
                  </a>
                  <a
                    href="mailto:info@computerhellen.de"
                    className="flex items-start gap-3 rounded-xl bg-secondary/60 p-4 hover:bg-secondary transition"
                    data-testid="hero-card-email"
                  >
                    <Mail className="mt-0.5 h-5 w-5 text-primary" />
                    <div>
                      <div className="text-sm font-semibold">E-Mail</div>
                      <div className="text-sm text-muted-foreground">info@computerhellen.de</div>
                    </div>
                  </a>
                  <div className="flex items-start gap-3 rounded-xl bg-secondary/60 p-4">
                    <MapPin className="mt-0.5 h-5 w-5 text-primary" />
                    <div>
                      <div className="text-sm font-semibold">Standort</div>
                      <div className="text-sm text-muted-foreground">Dinkelweg 21, 47638 Straelen</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-primary/20 blur-2xl" />
              <div className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-accent/30 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* LEISTUNGEN */}
      <section id="leistungen" className="scroll-mt-24 py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary">
              Leistungen
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl lg:text-5xl" data-testid="services-title">
              Alles rund um Ihre IT – aus einer Hand
            </h2>
            <p className="mt-4 text-base text-muted-foreground sm:text-lg">
              Vom langsamen Notebook bis zur kompletten Netzwerklösung: Ich kümmere mich
              persönlich darum, dass Ihre Technik einfach funktioniert.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`service-card-${i}`}
                  className="svc-card rounded-2xl border border-border bg-white p-6"
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ÜBER */}
      <section id="ueber" className="scroll-mt-24 bg-secondary/40 py-20 lg:py-28">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8 items-center">
          <div>
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary">
              Über mich
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl lg:text-5xl" data-testid="about-title">
              IT, die mitdenkt.
              <br /> Mensch, der zuhört.
            </h2>
            <p className="mt-5 text-base text-muted-foreground sm:text-lg">
              Mein Name ist <strong className="text-foreground">Alexander Hellen</strong>,
              Diplom-Ingenieur und seit 2004 selbstständig im Bereich IT-Dienstleistungen
              am Niederrhein. Bei mir bekommen Sie keinen Servicemitarbeiter Nummer 47 –
              sondern einen festen Ansprechpartner, der Ihre Systeme kennt.
            </p>
            <p className="mt-4 text-base text-muted-foreground sm:text-lg">
              Ob Privatkunde, Selbstständiger oder kleines Unternehmen: Ich nehme mir Zeit,
              erkläre verständlich und finde Lösungen, die zu Ihrem Alltag passen –
              nicht zu meinem Katalog.
            </p>

            <ul className="mt-8 space-y-3">
              {advantages.map((a, i) => (
                <li key={a} className="flex items-start gap-3" data-testid={`advantage-${i}`}>
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary shrink-0" />
                  <span className="text-foreground">{a}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <div className="relative overflow-hidden rounded-3xl border border-border bg-white shadow-xl">
              <div className="aspect-square w-full bg-[#E6F0FA]">
                <img
                  src="/assets/portrait-cartoon.png"
                  alt="Alexander Hellen – Inhaber Computer Hellen"
                  className="h-full w-full object-cover"
                  data-testid="about-portrait"
                />
              </div>
              <div className="p-6 border-t border-border">
                <div className="font-display text-lg font-bold">Alexander Hellen</div>
                <div className="text-sm text-muted-foreground">Dipl.-Ing. · Inhaber</div>
                <dl className="mt-5 grid grid-cols-2 gap-5">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Region</dt>
                    <dd className="mt-1 font-display text-base font-semibold">Niederrhein</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Selbstständig seit</dt>
                    <dd className="mt-1 font-display text-base font-semibold">2004</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Form</dt>
                    <dd className="mt-1 font-display text-base font-semibold">Einzelunternehmen</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fokus</dt>
                    <dd className="mt-1 font-display text-base font-semibold">IT · Netzwerk · TK</dd>
                  </div>
                </dl>
              </div>
            </div>
            <div className="absolute -top-6 -right-6 -z-10 h-32 w-32 rounded-full bg-primary/10 blur-2xl" />
            <div className="absolute -bottom-8 -left-8 -z-10 h-40 w-40 rounded-full bg-accent/20 blur-3xl" />
          </div>
        </div>
      </section>

      {/* KONTAKT */}
      <section id="kontakt" className="scroll-mt-24 py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="rounded-3xl border border-border bg-white p-8 shadow-xl sm:p-12 lg:p-16">
            <div className="grid gap-10 lg:grid-cols-2 lg:gap-16 items-start">
              <div>
                <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary">
                  Kontakt
                </span>
                <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl lg:text-5xl" data-testid="contact-title">
                  Sprechen wir.
                </h2>
                <p className="mt-4 text-base text-muted-foreground sm:text-lg">
                  Für Anfragen und Terminvereinbarung erreichen Sie mich am besten telefonisch
                  oder per E-Mail. Falls ich gerade nicht ans Telefon gehen kann, nimmt der
                  Anrufbeantworter Ihre Nachricht entgegen – ich rufe verlässlich zurück.
                </p>
              </div>

              <div className="space-y-4">
                <a
                  href="tel:028349448700"
                  data-testid="contact-phone-card"
                  className="group flex items-center justify-between gap-4 rounded-2xl border border-border bg-secondary/40 p-5 hover:border-primary hover:bg-secondary transition"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Phone className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Telefon</div>
                      <div className="font-display text-lg font-semibold">02834 9448700</div>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition" />
                </a>

                <a
                  href="mailto:info@computerhellen.de"
                  data-testid="contact-email-card"
                  className="group flex items-center justify-between gap-4 rounded-2xl border border-border bg-secondary/40 p-5 hover:border-primary hover:bg-secondary transition"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">E-Mail</div>
                      <div className="font-display text-lg font-semibold">info@computerhellen.de</div>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition" />
                </a>

                <div
                  className="flex items-center gap-4 rounded-2xl border border-border bg-secondary/40 p-5"
                  data-testid="contact-hours-card"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sprechzeiten</div>
                    <div className="font-display text-base font-semibold">
                      Mo – Fr · 10:00–12:00 & 15:00–17:00 Uhr
                    </div>
                  </div>
                </div>

                <div
                  className="flex items-center gap-4 rounded-2xl border border-border bg-secondary/40 p-5"
                  data-testid="contact-address-card"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Anschrift</div>
                    <div className="font-display text-base font-semibold">Dinkelweg 21, 47638 Straelen</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Mit dem Klick auf eine Kontaktoption werden Sie zu Ihrer Telefon- bzw. E-Mail-Anwendung weitergeleitet. Mehr dazu in der{" "}
            <Link to="/datenschutz" className="underline hover:text-primary">Datenschutzerklärung</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
